import styled from "styled-components";
import { Heading2Bold } from "../../../common/Typography/Heading2";
import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { UploadMusicProvider, useUploadMusic } from "./useUploadMusic";
import { Formik, FormikProps } from "formik";
import { IExtendedFile, IMusic } from "../../../common/commonTypes";

import { uploadTrackSchema } from "./UploadTrackSchema";
import UploadMusicForm from "./UploadMusicForm";
import { MusicEnum } from "../../../common/commonEnums";
import AlertMessage from "../../../common/components/AlertMessage";
import ConnectWalletModal from "../../../common/components/Modal/ConnectWalletModal/ConnectWalletModal";
import { useReceiveWalletSelect } from "../../../common/hooks/useReceiveWalletSelect";
import { useSession } from "../../../queries/authentication.query";

const Root = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  max-width: 1120px;
  padding: 0 20px 237px 20px;

  @media (max-width: ${({ theme }) => theme.breakpoints.laptop}) {
    max-width: 100%;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobileL}) {
    padding-bottom: 50px;
  }
`;

const Title = styled(Heading2Bold)`
  margin-bottom: 60px;
  margin-top: 32px;
`;

const ErrorWrapper = styled.div`
  margin-bottom: 20px;
`;

export interface MusicFormValues {
  masterWav?: IExtendedFile;
  masterMp3?: IExtendedFile;
  instrumental?: IExtendedFile;
  vocals?: IExtendedFile;
  cover?: IExtendedFile;
  poster?: IExtendedFile;
  name?: string;
  genres?: string[];
  countries?: string[];
  description?: string;
  languages?: string[];
  hidden: boolean;
}

interface UploadMusicProps {
  onUploadSuccess?: () => void;
}

const mapMusicToForm = (music: Partial<IMusic>): MusicFormValues => ({
  name: music.title,
  genres: music.genres || [],
  countries: music?.countries || [],
  description: music?.description,
  masterWav: undefined,
  masterMp3: undefined,
  cover: undefined,
  poster: undefined,
  instrumental: undefined,
  vocals: undefined,
  hidden: false,
  languages: music.languages || [],
});

const UploadMusicContent = ({ onUploadSuccess }: UploadMusicProps) => {
  const { t } = useTranslation();
  const { uploadTrack, isLoading, error, closeError } = useUploadMusic();
  const [uploadingItemsCount, setUploadingItemsCount] = useState(0);
  const onFileUploadStart = () => setUploadingItemsCount((items) => items + 1);
  const onFileUploadEnd = () => setUploadingItemsCount((items) => items - 1);
  const isFileUploading = uploadingItemsCount !== 0;

  const { user } = useSession();
  const userReceiveAddress = user?.address;
  const {
    selectWallet,
    isLoading: isWalletSaving,
    error: walletError,
  } = useReceiveWalletSelect();

  const musicData = {
    title: "",
    countries: [],
    description: "",
    master: undefined,
  } as Partial<IMusic>;
  const initialValues = mapMusicToForm(musicData);

  const isUploadDisabled =
    !userReceiveAddress || !!walletError || isFileUploading;

  const onSave = async (values: MusicFormValues) => {
    if (!isUploadDisabled) {
      await uploadTrack(
        {
          title: values.name,
          type: MusicEnum.SINGLE,
          countries: values.countries || [],
          genres: values.genres || [],
          description: values.description,
          languages: values.languages || [],
          master_length: values.masterWav?.meta?.duration,
          full_length: values.masterMp3?.meta?.duration,
          backing_length: values.instrumental?.meta?.duration,
          vocal_length: values.vocals?.meta?.duration,
          hidden: values.hidden,
        },
        onUploadSuccess
      );
    }
  };

  return (
    <>
      <Root>
        <Title>{t("upload-music")}</Title>
        <ErrorWrapper>
          <AlertMessage isOpen={!!error} message={error} onClose={closeError} />
        </ErrorWrapper>

        <Formik
          onSubmit={onSave}
          initialValues={initialValues}
          validationSchema={uploadTrackSchema}
          validateOnChange
          validateOnBlur
          validateOnMount
        >
          {(formikProps: FormikProps<MusicFormValues>) => (
            <>
              <UploadMusicForm
                {...formikProps}
                isLoading={isLoading}
                isDisabled={isUploadDisabled}
                onUploadStart={onFileUploadStart}
                onUploadEnd={onFileUploadEnd}
              />
            </>
          )}
        </Formik>
      </Root>
      <ConnectWalletModal
        connectWallet={selectWallet}
        isOpen={!userReceiveAddress}
        isLoading={isWalletSaving}
      />
    </>
  );
};

const UploadMusic = (props: UploadMusicProps) => {
  return (
    <UploadMusicProvider>
      <UploadMusicContent {...props} />
    </UploadMusicProvider>
  );
};

export default UploadMusic;
